import React, { useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { confirmLoginCodeThunk, loginCodeThunk } from "reduxStore/auth/thunks";
import { ICodeLoginResponse } from "types/auth";
import { useAppDispatch } from "hooks/useAppDispatch";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { Messages, NotificationSeverity } from "helpers/constants";
import Notification from "utils/Notification";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { AuthContainer } from "components/Layout/AuthContainer";
import { GuideLink } from "pages/AuthGuides/GuideLink";
import { AdminRoutes } from "config/routes";

const ResetPasswordWrapper: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isAddingCode, setIsAddingCode] = useState(false);
  const [email, setEmail] = React.useState("");
  const [loadingMessage, setLoadingMessage] = React.useState("");

  const handleEmailSubmit = (
    values: ICodeLoginResponse,
    message: string = "Sending login code...",
  ) => {
    setLoadingMessage(message);
    setLoading(true);
    dispatch(loginCodeThunk(values)).then((result) => {
      console.log("loginCodeThunk result:", result);
      setIsAddingCode(true);
      setLoading(false);
      dispatch(
        showNotificationThunk({
          message: Messages.successLoginCode,
          severity: NotificationSeverity.success,
        }),
      );
    });
  };

  const handleCodeSubmit = (values: ICodeLoginResponse) => {
    setLoadingMessage("Verifying login code...");
    setLoading(true);
    dispatch(confirmLoginCodeThunk(values)).then((result) => {
      console.log("confirmLoginCodeThunk result:", result);
      switch (result.type) {
        case "auth/confirmLoginCodeThunk/fulfilled":
          window.location.reload();
          setLoading(false);
          break;
        case "auth/confirmLoginCodeThunk/rejected":
          dispatch(
            showNotificationThunk({
              message: Messages.errorInvalidLoginCode,
              severity: NotificationSeverity.error,
            }),
          );
          setIsAddingCode(false);

          setLoading(false);
          break;
      }
    });
  };

  const handleSubmit = async (event: {
    preventDefault: () => void;
    currentTarget: HTMLFormElement | undefined;
  }) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const values: ICodeLoginResponse = {
      email: data.get("email")?.toString()?.trim() || "",
      code: data.get("code"),
    };
    if (isAddingCode) {
      handleCodeSubmit(values);
      return;
    }
    handleEmailSubmit(values);
  };

  return (
    <AuthContainer loading={loading} loadingText={loadingMessage}>
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography component="h1" variant="h6" sx={{ textAlign: "center" }}>
          {isAddingCode
            ? "Enter Your Login Code"
            : "Enter Your Email to Receive a Single Time Login code"}
        </Typography>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {isAddingCode && (
            <TextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              id="code"
              label="Single Time Code"
              name="code"
              autoFocus
            />
          )}

          {!isAddingCode && (
            <TextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
            />
          )}

          <Box sx={{ m: 1, position: "relative" }}>
            <Button
              disabled={loading}
              type="submit"
              fullWidth
              color="primary"
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Submit
            </Button>
            {loading && (
              <CircularProgress
                size={30}
                sx={{
                  color: green[500],
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>

          {isAddingCode && (
            <Button
              disabled={loading}
              onClick={() =>
                handleEmailSubmit({ email }, "Requesting new login code...")
              }
              fullWidth
              variant="outlined"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
            >
              Request a New Code
            </Button>
          )}

          <GuideLink
            to={AdminRoutes.LoginCodeGuide}
            label="View step-by-step guide"
          />
          <Button
            disabled={loading}
            fullWidth
            variant="text"
            color="primary"
            sx={{ mt: 1, mb: 2 }}
            onClick={() => navigate("/")}
          >
            Go to Login
          </Button>
        </Box>
        <Notification onClose={() => {}} />
      </Box>
    </AuthContainer>
  );
};

export default ResetPasswordWrapper;
